import {
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalOverlay,
    Progress,
    Text,
    VStack,
    Alert,
    AlertIcon,
    AlertDescription,
} from "@chakra-ui/react";
import { IconCircleCheck, IconAlertTriangle } from "@tabler/icons-react";
import type { BulkOperationState } from "../../api/hooks/useBulkOperation";

interface BulkProgressModalProps {
    isOpen: boolean;
    operationLabel: string;
    state: BulkOperationState;
    onDone: () => void;
}

export function BulkProgressModal({ isOpen, operationLabel, state, onDone }: BulkProgressModalProps) {
    const { isRunning, completed, total, currentLabel, errors, isDone } = state;
    const progressPercent = total > 0 ? (completed / total) * 100 : 0;
    const successCount = completed - errors.length;
    const hasErrors = errors.length > 0;

    return (
        <Modal isOpen={isOpen} onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false} isCentered size="md">
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent>
                <ModalHeader>{operationLabel}</ModalHeader>
                <ModalBody>
                    <VStack align="stretch" spacing={4}>
                        <Box>
                            <Flex justify="space-between" mb={1}>
                                <Text fontSize="sm" color="gray.600">
                                    {isDone ? "Completed" : `Processing ${completed + 1} of ${total}…`}
                                </Text>
                                <Text fontSize="sm" color="gray.600">
                                    {completed} / {total}
                                </Text>
                            </Flex>
                            <Progress
                                value={progressPercent}
                                size="sm"
                                colorScheme={isDone && hasErrors ? "orange" : "blue"}
                                borderRadius="full"
                                isAnimated={isRunning}
                            />
                        </Box>

                        {isRunning && currentLabel && (
                            <Text fontSize="sm" color="gray.500" noOfLines={1}>
                                Processing <strong>{currentLabel}</strong>…
                            </Text>
                        )}

                        {isDone && (
                            <HStack spacing={2} color={hasErrors ? "orange.500" : "green.500"}>
                                <Icon as={hasErrors ? IconAlertTriangle : IconCircleCheck} boxSize={5} />
                                <Text fontSize="sm" fontWeight="medium">
                                    {hasErrors
                                        ? `${successCount} succeeded, ${errors.length} failed`
                                        : `All ${total} operation${total !== 1 ? "s" : ""} completed successfully`}
                                </Text>
                            </HStack>
                        )}

                        {isDone && hasErrors && (
                            <Box maxH="160px" overflowY="auto">
                                <VStack align="stretch" spacing={2}>
                                    {errors.map((err, i) => (
                                        <Alert key={i} status="error" size="sm" borderRadius="md" py={1} px={2}>
                                            <AlertIcon boxSize={3} />
                                            <AlertDescription fontSize="xs">
                                                <strong>{err.label}</strong>: {err.message}
                                            </AlertDescription>
                                        </Alert>
                                    ))}
                                </VStack>
                            </Box>
                        )}
                    </VStack>
                </ModalBody>

                {isDone && (
                    <ModalFooter>
                        <Button colorScheme="blue" onClick={onDone}>
                            Done
                        </Button>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    );
}
